// src/components/Expense/EditExpenseModal.jsx
// Modal component for editing a single expense

import React, { useState, useEffect } from 'react';

/**
 * EditExpenseModal Component
 * A modal dialog for editing a single expense
 * 
 * @param {boolean} isOpen - Controls whether modal is visible
 * @param {Function} onClose - Callback when modal should close
 * @param {Function} onSave - Callback when form is submitted
 * @param {Object} expense - Existing expense data (for edit mode)
 * @param {boolean} isSaving - Whether save operation is in progress
 */
const EditExpenseModal = ({ isOpen, onClose, onSave, expense, isSaving }) => {

  // Expense types
  const EXPENSE_TYPES = ['SALARY', 'ELECTRICITY', 'FUEL'];

  // Form state
  const [formData, setFormData] = useState({
    type: 'SALARY',
    reason: '',
    amount: ''
  });

  // Populate form when editing
  useEffect(() => {
    if (expense) {
      setFormData({
        type: expense.type || 'SALARY',
        reason: expense.reason || '',
        amount: expense.amount ? expense.amount.toString() : ''
      });
    } else {
      setFormData({
        type: 'SALARY',
        reason: '',
        amount: ''
      });
    }
  }, [expense]);

  if (!isOpen) return null;

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.type) {
      alert('Please select an expense type');
      return;
    }

    if (!formData.reason.trim()) {
      alert('Please enter a reason');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    // Send single object only
    onSave({
      type: formData.type,
      reason: formData.reason,
      amount: parseFloat(formData.amount)
    });

    // Clear form after success
    setFormData({
      type: 'SALARY',
      reason: '',
      amount: ''
    });
  };

  // Handle modal close
  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  return (
    <div className="inventory-modal-overlay">
      <div className="inventory-modal-content">
        {/* Modal Header */}
        <header className="inventory-modal-header">
          <h3 className="inventory-modal-title">
            {expense ? `Edit Expense: ${expense.type}` : 'Edit Expense'}
          </h3>
          <button
            className="inventory-close-modal-btn"
            onClick={handleClose}
            disabled={isSaving}
            type="button"
          >
            ✕
          </button>
        </header>

        {/* Modal Body */}
        <div className="inventory-modal-body">
          <form id="expenseForm" onSubmit={handleSubmit}>
            <div className="inventory-form-grid">
              
              {/* Expense Type Field */}
              <label htmlFor="type">
                Expense Type <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                id="type"
                name="type"
                className="inventory-custom-input"
                value={formData.type}
                onChange={handleChange}
                required
                disabled={isSaving}
              >
                {EXPENSE_TYPES.map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>

              {/* Reason Field */}
              <label htmlFor="reason">
                Reason <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                id="reason"
                name="reason"
                className="inventory-custom-input"
                placeholder="e.g., Employee salary, Office electricity bill"
                value={formData.reason}
                onChange={handleChange}
                required
                disabled={isSaving}
              />

              {/* Amount Field */}
              <label htmlFor="amount">
                Amount <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="number"
                id="amount"
                name="amount"
                className="inventory-custom-input"
                placeholder="0.00"
                value={formData.amount}
                onChange={handleChange}
                step="0.01"
                min="0"
                required
                disabled={isSaving}
              />
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <footer className="inventory-modal-footer">
          <button
            className="inventory-btn inventory-btn-cancel"
            onClick={handleClose}
            disabled={isSaving}
            type="button"
          >
            Cancel
          </button>
          <button
            className="inventory-btn inventory-btn-save"
            disabled={isSaving}
            type="submit"
            form="expenseForm"
          >
            {isSaving ? 'Saving...' : 'Save Expense'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default EditExpenseModal;